import React, { type FormEvent, type ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import classNames from 'classnames'
import { Button } from '../../../components/Button/Button'
import { ReactComponent as StepArrow } from '../../../assets/images/wizard-step-arrow.svg'
import styles from './WizardStepCard.module.scss'

export interface WizardStepAction {
  label: string
  disabled: boolean
  loading?: boolean
  showEnterHint?: boolean
  align?: 'end' | 'center'
}

interface WizardStepHeaderProps {
  icon: string
  title: string
  subtitle?: string
}

export const WizardStepHeader: React.FC<WizardStepHeaderProps> = ({ icon, title, subtitle }) => (
  <div className='d-flex align-items-center zocks-gap-0.75'>
    <div
      className={classNames(
        'd-flex align-items-center justify-content-center border zocks-border-color-gray-200 zocks-border-0.5 zocks-bg-color-gray-100 flex-shrink-0',
        styles.iconBox,
      )}
    >
      <i className={classNames('bi fs-6 zocks-text-gray-900', icon)} aria-hidden='true' />
    </div>
    <div className='d-flex flex-column zocks-gap-0.25'>
      <span className='paragraph-medium fw-600 zocks-text-gray-900'>{title}</span>
      {subtitle ? <span className='paragraph-small zocks-text-gray-600'>{subtitle}</span> : null}
    </div>
  </div>
)

interface WizardStepCardProps extends WizardStepHeaderProps {
  showConnector?: boolean
  action?: WizardStepAction
  onSubmit?: () => void
  children?: ReactNode
}

export const WizardStepCard: React.FC<WizardStepCardProps> = ({ icon, title, subtitle, showConnector = true, action, onSubmit, children }) => {
  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (action && !action.disabled && !action.loading) {
      onSubmit?.()
    }
  }

  return (
    <>
      {showConnector ? (
        <div className='d-flex py-3 zocks-px-1.25' aria-hidden='true'>
          <StepArrow />
        </div>
      ) : null}
      <Form
        noValidate
        onSubmit={handleSubmit}
        className='bg-white p-3 pb-4 d-flex flex-column gap-4 border zocks-border-color-gray-200 zocks-border-0.75'
        aria-label={title}
      >
        <WizardStepHeader icon={icon} title={title} subtitle={subtitle} />
        {children}
        {action ? (
          <div className={classNames('d-flex align-items-center pt-3', action.align === 'center' ? 'justify-content-center' : 'justify-content-end')}>
            <Button
              type='submit'
              variant='primary'
              className='d-flex align-items-center gap-2 zocks-px-0.75'
              disabled={action.disabled}
              loading={action.loading}
            >
              {action.label}
              {action.showEnterHint ? <i className='bi bi-arrow-return-left fs-6' aria-hidden='true' /> : null}
            </Button>
          </div>
        ) : null}
      </Form>
    </>
  )
}
